import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Checkmark from "../../components/Checkmark/Checkmark";
import heroFrcophthTopics from "../../assets/oq/hero-frcophth-topics.png";
import aboutPurposeBuiltQuestion from "../../assets/oq/about-purpose-built-question.png";
import dashboardPage from "../../assets/oq/Dashboard.png";
import "./AboutPage.scss";

const AboutPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div id="AboutPage" className="product-page--oq">
      <NavBar />

      <header className="tml-hero about-hero" aria-label="About OphthalmologyQ">
        <div className="site-container">
          <p className="tml-eyebrow">OphthalmologyQ</p>
          <h1 className="tml-hero__title">
            About <span className="tml-hero__title-accent">us</span>
          </h1>
          <p className="tml-hero__lead">
            A platform <strong>co-built by ophthalmologists, for ophthalmologists</strong>—helping you prepare
            for exams and interviews with syllabus depth, exam-realistic questions, and UK-aligned support.
          </p>
        </div>
      </header>

      <div className="product-sales-content">
        <div className="site-container">
          <section
            id="about-oq"
            className="sales-section about-overview"
            aria-labelledby="about-overview-heading"
          >
            <div className="content-with-image">
              <div className="content-side">
                <div className="section-header">
                  <h2 id="about-overview-heading">Overview</h2>
                </div>
                <p className="lead-text">
                  Know what the papers feel like before you sit them. Our <strong>FRCOphth Part 1 and Part 2</strong>{" "}
                  question banks test <strong>every module and every area</strong> within those modules—so gaps do not
                  surprise you on the day.
                </p>
                <ul className="about-feature-list">
                  <li>
                    <Checkmark />
                    <span>
                      <strong>FRCOphth Part 1 &amp; Part 2</strong> banks: full syllabus depth—every module, every area.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      <strong>Exam-style questions</strong> that match the wording and format of real exam questions.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      <strong>Timed practice</strong> and full <strong>mock exams</strong> to rehearse under exam
                      conditions.
                    </span>
                  </li>
                  <li>
                    <Checkmark />
                    <span>
                      Instant, detailed feedback with explanations aligned to UK guidelines—plus analytics on where you
                      stand.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="image-side">
                <img
                  src={heroFrcophthTopics}
                  alt="FRCOphth Part 1 Topics dashboard with modules, progress, and practice options"
                  className="product-screenshot"
                  width={1024}
                  height={732}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      <section id="why-us" className="about-section about-section--alt">
        <div className="site-container">
          <div className="about-split about-split--reverse">
            <div className="about-split__text">
              <p className="about-section__label">Why specialists choose us</p>
              <h2 className="about-section__title">Purpose-built for ophthalmology</h2>
              <p className="about-section__intro">
                OphthalmologyQ is <strong>co-built by ophthalmologists for ophthalmologists</strong>—a dedicated
                platform, not a generic medical bank with an ophthalmology add-on.
              </p>
              <ul className="about-feature-list">
                <li>
                  <Checkmark />
                  <span>
                    <strong>Co-built by ophthalmologists for ophthalmologists</strong>—clinical rigour in every
                    question.
                  </span>
                </li>
                <li>
                  <Checkmark />
                  <span>
                    <strong>Interview coaching</strong> delivered by <strong>leading UK ophthalmologists</strong> who
                    know the ST1 process inside out.
                  </span>
                </li>
                <li>
                  <Checkmark />
                  <span>
                    Thousands of questions across <strong>FRCOphth Parts 1 &amp; 2</strong>, <strong>MSRA</strong>,
                    interviews, and more—regularly updated.
                  </span>
                </li>
                <li>
                  <Checkmark />
                  <span>
                    <strong>Timed mock exams</strong> and flexible practice so you can build speed and accuracy on your
                    schedule.
                  </span>
                </li>
              </ul>
            </div>
            <div className="about-split__media">
              <div className="about-media-frame">
                <img
                  src={aboutPurposeBuiltQuestion}
                  alt="OphthalmologyQ practice question with detailed explanation and answer feedback"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="our-vision" className="about-section about-section--vision">
        <div className="site-container">
          <p className="about-section__label about-section__label--center">Coverage</p>
          <h2 className="about-section__title about-section__title--center">Our vision</h2>
          <p className="about-vision__lead">
            We want you to walk into the exam having already practised the <strong>full syllabus under exam
            conditions</strong>—with <strong>exam-style questions</strong>, <strong>timed mocks</strong>, and support
            from <strong>UK ophthalmology specialists</strong> at every stage of your preparation.
          </p>

          <div className="about-vision__chips">
            <div className="about-vision-chip">
              <Checkmark />
              <span>MSRA</span>
            </div>
            <div className="about-vision-chip">
              <Checkmark />
              <span>Coaching</span>
            </div>
            <div className="about-vision-chip">
              <Checkmark />
              <span>FRCOphth Part 1</span>
            </div>
            <div className="about-vision-chip">
              <Checkmark />
              <span>FRCOphth Part 2</span>
            </div>
          </div>

          <div className="about-dashboard-showcase">
            <img src={dashboardPage} alt="Dashboard showing your study progress" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
